import { WalletAdapterNetwork, WalletError } from '@solana/wallet-adapter-base';
import { ConnectionProvider, WalletProvider as SolanaWalletProvider, useWallet } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { BackpackWalletAdapter } from '@solana/wallet-adapter-backpack';
import { BraveWalletAdapter } from '@solana/wallet-adapter-brave';
import { clusterApiUrl } from '@solana/web3.js';
import { useMemo, useCallback, createContext, useContext, useState, useEffect } from 'react';
import { UserAccount } from '../types/api';
import { authApi } from '../services/auth';

// Import the styles
import '@solana/wallet-adapter-react-ui/styles.css';

interface WalletProviderProps {
  children: React.ReactNode;
}

interface AccountContextType {
  account: UserAccount | null;
  isLoading: boolean;
  error: string | null;
  refreshBalance: () => Promise<void>;
  setAccount: (account: UserAccount | null) => void;
  disconnect: () => void;
}

const AccountContext = createContext<AccountContextType>({
  account: null,
  isLoading: false,
  error: null,
  refreshBalance: async () => {},
  setAccount: () => {},
  disconnect: () => {},
});

export const useAccount = () => useContext(AccountContext);

function AccountProvider({ children }: WalletProviderProps) {
  const { publicKey } = useWallet();
  const [account, setAccount] = useState<UserAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshBalance = useCallback(async () => {
    if (!account?.wallet_address) return;
    
    try {
      const { account: updatedAccount } = await authApi.getAccount(account.wallet_address);
      setAccount(updatedAccount);
    } catch (err) {
      console.error('Failed to refresh balance:', err);
    }
  }, [account?.wallet_address]);

  const handleWalletConnection = useCallback(async (publicKey: string) => {
    if (!publicKey) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { account } = await authApi.connect(publicKey);
      setAccount(account);
    } catch (err) {
      console.error('Failed to connect wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect wallet');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleWalletDisconnect = useCallback(() => {
    setAccount(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (publicKey) {
      handleWalletConnection(publicKey.toString());
    } else {
      handleWalletDisconnect();
    }
  }, [publicKey, handleWalletConnection, handleWalletDisconnect]);

  const disconnect = useCallback(() => {
    setAccount(null);
    useWallet().disconnect();
  }, []);

  return (
    <AccountContext.Provider value={{ account, isLoading, error, refreshBalance, setAccount, disconnect }}>
      {children}
    </AccountContext.Provider>
  );
}

export function WalletProvider({ children }: WalletProviderProps) {
  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new BackpackWalletAdapter(),
      new BraveWalletAdapter(),
    ],
    []
  );

  const onError = useCallback((error: WalletError) => {
    console.error('Wallet error:', error);
    if (error.name === 'WalletConnectionError') {
      console.log('Please check if your wallet is properly connected and unlocked');
    }
    if (error.message.includes('connection refused')) {
      console.log('Connection to wallet failed. Please try:');
      console.log('1. Unlock your wallet');
      console.log('2. Switch to Mainnet network in your wallet');
      console.log('3. Refresh the page');
    }
  }, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <SolanaWalletProvider 
        wallets={wallets} 
        autoConnect={true}
        onError={onError}
      >
        <WalletModalProvider>
          <AccountProvider>
            {children}
          </AccountProvider>
        </WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
} 